<template>
  <template v-if="postResult?.lead">
    <p class="lead" v-text="postResult.lead" />
    <hr />
  </template>
  <SanityBlock :blocks="formattedBody" />
  <SanityBlockTable v-if="postResult?.table" :rows="postResult?.table?.rows" />
</template>

<script lang="ts" setup>
import { useStore } from 'vuex'
import { slugify } from 'transliteration'
import { POST_BY_SLUG_QUERY } from '@/groq/queries'

definePageMeta({
  layout: 'blog-post',
})

const route = useRoute()
const store = useStore()

const {
  data: postResult,
  pending: postLoading,
  refresh: postRefresh,
} = await useSanityQuery(POST_BY_SLUG_QUERY, { slug: route.params?.slug.toString() })

// /blog/[slug] is the project's only dynamic route, so a mistyped or retired
// slug matches the route but resolves to no Sanity document. The minimal Sanity
// client returns an empty object ({}, truthy) rather than null for a [0] miss,
// so guard on the document's _id: without it the downstream render dereferences
// post.body.map() and throws a 500 — surface a real 404 (and error page) instead.
if (!postResult.value?._id) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

// Article social image: the post's Sanity mainImage as an absolute CDN URL,
// cropped to 1200x630 and pinned to jpg (some mainImages are SVG, which the
// social crawlers won't render — jpg forces Sanity to rasterise), instead of
// the site-wide /images/og-image.png default.
const { $urlFor } = useNuxtApp()
const ogImage = postResult.value?.mainImage
  ? $urlFor(postResult.value.mainImage).width(1200).height(630).fit('crop').format('jpg').url()
  : undefined

useCustomServerMeta({
  title: postResult.value?.pageTitle || postResult.value?.title,
  description: postResult.value?.pageDescription || postResult.value?.lead,
  type: 'article',
  image: ogImage,
})

useSchemaOrg([
  defineArticle({
    '@type': 'BlogPosting',
    datePublished: postResult.value?._createdAt && new Date(postResult.value?._createdAt).toISOString(),
    dateModified: postResult.value?._updatedAt && new Date(postResult.value?._updatedAt).toISOString(),
    author: postResult.value?.author?.name && [
      {
        name: postResult.value.author.name,
      },
    ],
  }),
  defineBreadcrumb({
    itemListElement: [
      { name: 'Credsy', item: '/' },
      { name: 'Blog', item: '/blog' },
      { name: postResult.value?.pageTitle || postResult.value?.title },
    ],
  }),
])

const validPost = computed<boolean>(() => !!postResult.value)

const formattedBody = computed<unknown>(() =>
  postResult.value?.body.map((block) => {
    if (['h2', 'h3'].includes(block.style)) {
      return { ...block, id: slugify(block.children[0].text) }
    }

    return block
  })
)

watch(
  () => [route.params.slug],
  ([slug]) => {
    postRefresh({ slug: slug.toString() })
  }
)

watchEffect(() => {
  if (postLoading.value) {
    return
  }

  if (!validPost.value) postRefresh({ slug: route.params.slug.toString() })
})

watch(
  postResult,
  (post) => {
    if (!post) return

    store.commit('setBlogPostNavigation', { body: post.body })
    store.commit('setBlogPost', {
      id: post._id,
      title: post.title,
      createdAt: post._createdAt,
      mainImage: post.mainImage,
      updatedAt: post._updatedAt,
      author: post.author,
      type: post.type,
      categories: post.categories,
      color: post.color || '',
      table: post.table,
    })
    store.commit('setPage', { title: post.title, fullPath: route.fullPath })
  },
  { immediate: true }
)
</script>
